// Response schemas for guides, kit files and search (plan §4, T34 and T35). Unknown keys are
// dropped. Guide blocks stay `unknown` here: the web renderer checks every block and link itself
// and never renders HTML, so one odd block cannot fail the whole page.
import { z } from 'zod';

const MAX_TEXT = 20_000;
const MAX_LABEL = 500;
const MAX_KEY = 200;
const SHA256_HEX = 64;

const audienceSchema = z.enum(['team', 'leads']);

const pageOf = <T extends z.ZodType>(item: T) =>
  z.object({
    items: z.array(item),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
  });

export const guideSummarySchema = z.object({
  slug: z.string().min(1).max(MAX_KEY),
  title: z.string().max(MAX_LABEL),
  audience: audienceSchema,
});
export type GuideSummary = z.infer<typeof guideSummarySchema>;

export const guideSchema = guideSummarySchema.extend({ blocks: z.array(z.unknown()) });
export type Guide = z.infer<typeof guideSchema>;

export const guidePageSchema = pageOf(guideSummarySchema);
export type GuidePage = z.infer<typeof guidePageSchema>;

export const kitFileSchema = z.object({
  id: z.number().int().positive(),
  title: z.string().max(MAX_LABEL),
  fileName: z.string().min(1).max(MAX_LABEL),
  bytes: z.number().int().min(0),
  sha256: z.string().length(SHA256_HEX),
  audience: audienceSchema,
});
export type KitFile = z.infer<typeof kitFileSchema>;

export const kitFilePageSchema = pageOf(kitFileSchema);
export type KitFilePage = z.infer<typeof kitFilePageSchema>;

export const SEARCH_KINDS = [
  'task',
  'step',
  'evidence_line',
  'safeguard',
  'guide',
  'lesson',
  'checklist',
  'comment',
  'risk',
  'acceptance',
  'ask',
  'decision',
  'kpi',
  'gate',
  'question',
] as const;
export type SearchKind = (typeof SEARCH_KINDS)[number];

export const searchResultSchema = z.object({
  kind: z.enum(SEARCH_KINDS),
  key: z.string().max(MAX_KEY),
  title: z.string().max(MAX_TEXT),
  snippet: z.string().max(MAX_TEXT),
  href: z.string().max(MAX_LABEL).nullable(),
});
export type SearchResult = z.infer<typeof searchResultSchema>;

export const searchPageSchema = pageOf(searchResultSchema);
export type SearchPage = z.infer<typeof searchPageSchema>;
